/**
 * AppLayout - Main application layout with sidebar
 * 
 * Provides the main layout structure for authenticated app routes
 * including sidebar, header, and content area.
 */
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SidebarProvider } from '../../../context/sidebar';
import { useAuth } from '../../../context/auth';
import Header from '../../header/app/Header.jsx';
import Sidebar from '../../header/app/Sidebar.jsx';
import Backdrop from '../../header/app/Backdrop.jsx';
import AlertsService from '../../header/app/AlertsService.jsx';
import NamespaceModal from '../../namespaces/namespace/NamespaceModal';
import AuthLoadingScreen from '../AuthLoadingScreen';

function AppLayout() {
  const location = useLocation();
  const { user, loading: authLoading, isProfileLoaded } = useAuth();
  const [showAuthLoading, setShowAuthLoading] = useState(false);
  
  // Check if user just signed in and we should show loading screen longer
  const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
  const isComingFromAuth = location.state?.from?.pathname === '/login' || 
                          location.state?.from?.pathname === '/signin' ||
                          justLoggedIn;
  
  // For premium users coming from auth, show loading screen for 1 second
  useEffect(() => {
    if (user && isComingFromAuth && (user.planLevel === 'premium' || user.isEnterprise)) {
      setShowAuthLoading(true);
      
      const timer = setTimeout(() => {
        setShowAuthLoading(false);
        // Clear the justLoggedIn flag after the loading period
        sessionStorage.removeItem('justLoggedIn');
      }, 1000);
      
      return () => clearTimeout(timer);
    } else {
      setShowAuthLoading(false);
    }
  }, [user, isComingFromAuth]);
  
  // Special case for main pricing page - no sidebar needed
  if (location.pathname === '/pricing') {
    return <Outlet />;
  }
  
  // Show full-screen loading when auth is loading or profile isn't loaded yet
  // This prevents the sidebar from flashing before auth state is determined
  if (authLoading || (user && !isProfileLoaded)) {
    console.log('AppLayout: Auth loading or profile not loaded, showing full-screen loader');
    return <AuthLoadingScreen />;
  }
  
  // Show extended loading for premium/pro users from auth
  if (showAuthLoading) {
    console.log('AppLayout: Premium user from auth, showing extended loading screen');
    return <AuthLoadingScreen />;
  }
  
  // Render the full layout structure
  console.log('AppLayout: Rendering full layout with sidebar');
  return (
    <SidebarProvider>
      <div className="flex flex-col h-screen bg-white dark:bg-gray-900">
        <AlertsService />
        <div className="flex flex-1 h-screen overflow-hidden">
          <Sidebar />
          <div className="flex-1 flex flex-col h-screen overflow-hidden">
            <Header />
            <Backdrop />
            <div className="flex-1 overflow-y-auto">
              <Outlet />
            </div>
          </div>
        </div>
        <NamespaceModal />
      </div>
    </SidebarProvider>
  );
}

export default AppLayout;

